/**
 * AuthCollageBackground — the web twin of the iOS sign-in wall. Behind
 * sign-in, account creation, early access and the marketing hero.
 *
 * Columns of the studio's own photography drift slowly up and down
 * (alternating direction), warmed by a colourful aurora and faded to
 * black toward the form so it never reads dull.
 *
 * WALL-2 (Rennan, Sep 29): new photographs, a new random order on every
 * visit, and never the same photograph on screen twice at once. The old
 * wall looped two photos per column three times over, so on a tall
 * screen a column showed the same frame twice. Now every column gets its
 * own photos, and enough of them that a photo's looping copy is always
 * off screen — the counts come from the wall's measured size
 * (planAuthWall in ./authWall).
 *
 * Decorative only: pointer-events-none, aria-hidden, lazy-loaded.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { tileColour } from './entryMotion';
import { authWallUrl, planAuthWall, shuffleWallPhotos, wallColumn, type WallPlan } from './authWall';

// Bottom-loaded fade tuned for Auth: bright reveal band near the
// wordmark (~22vh), solid black under the form. Other pages reusing
// this component pass their own `scrim`, shaped for their own layout.
const DEFAULT_SCRIM =
  "linear-gradient(to bottom, #000 0%, rgba(0,0,0,.5) 9%," +
  "rgba(0,0,0,.14) 22%, rgba(0,0,0,.5) 38%, rgba(0,0,0,.88) 50%," +
  "#000 66%, #000 100%)";

/** Six columns from `md` up, three below — as the wall always had. */
const preferredColumns = (width: number) => (width >= 768 ? 6 : 3);

const viewport = () =>
  typeof window === 'undefined' ? { w: 1280, h: 800 } : { w: window.innerWidth, h: window.innerHeight };

export function AuthCollageBackground({
  scrim = DEFAULT_SCRIM,
}: { scrim?: string } = {}) {
  const wallRef = useRef<HTMLDivElement>(null);
  // One random order per visit, kept for the life of the page, so a
  // resize never reshuffles what's already on screen.
  const [photos] = useState(shuffleWallPhotos);
  // First paint plans from the window; the wall's own size replaces it
  // before the browser paints (layout effect).
  const [plan, setPlan] = useState<WallPlan>(() => {
    const { w, h } = viewport();
    return planAuthWall(w, h, photos.length, preferredColumns(w));
  });

  useLayoutEffect(() => {
    const el = wallRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      const next = planAuthWall(width, height, photos.length, preferredColumns(width));
      // Grow, never shrink, while the column count holds: a longer strip
      // only makes a repeat less likely, and the photos on screen stay put.
      setPlan((prev) =>
        prev.columns === next.columns && prev.perColumn >= next.perColumn ? prev : next,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [photos.length]);

  return (
    <div
      ref={wallRef}
      className="pointer-events-none absolute inset-0 overflow-hidden bg-black"
      aria-hidden
    >
      <div className="absolute inset-0 flex gap-2 px-2">
        {Array.from({ length: plan.columns }, (_, i) => {
          const column = wallColumn(photos, plan, i);
          return (
            <div
              key={i}
              // ENTRY-80s — `overflow-visible`, so a tile's neon glow can
              // spill past its column in the finale. The wall's own
              // `overflow-hidden` still clips the looping strip.
              className="relative min-w-0 flex-1 overflow-visible"
            >
              <div
                className={i % 2 === 0 ? 'auth-col-up' : 'auth-col-down'}
                // The strip is two copies of the column; one loop moves it
                // by one copy. Same drift speed as before (the old strip
                // moved two tiles every 34s + 8s per column).
                style={{ animationDuration: `${((34 + i * 8) * column.length) / 2}s` }}
              >
                {[0, 1].flatMap((copy) =>
                  column.map((id, n) => {
                    const j = copy * column.length + n;
                    return (
                      // SIGNUP-1 — each frame is a [data-tile] with a colour
                      // veil so the sign-in finale can turn it into its
                      // colour and whip it into the sphere (entryMotion.ts).
                      // ENTRY-80s — the tile no longer clips (the photo and
                      // the veil round their own corners), so the veil's
                      // box-shadow — the tile's neon glow — can show. Static,
                      // so it costs nothing until the veil's opacity brings
                      // it up, and it squeezes into a glowing rod with the
                      // tile.
                      <div
                        key={`${copy}-${id}`}
                        data-tile="1"
                        className="relative mb-2 aspect-[3/4] w-full rounded-2xl"
                      >
                        <img
                          src={authWallUrl(id)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                          className="h-full w-full rounded-2xl object-cover"
                        />
                        <div
                          data-veil="1"
                          className="absolute inset-0 rounded-2xl opacity-0"
                          style={{
                            background: tileColour(i * 7 + j),
                            boxShadow: `0 0 18px 2px ${tileColour(i * 7 + j)}, 0 0 64px 14px ${tileColour(i * 7 + j)}80`,
                          }}
                        />
                        {/* ENTRY-SMOOTH — the white heat the rod runs as it
                            flies (entryMotion fades it up; was a brightness
                            filter on the whole tile). */}
                        <div data-hot="1" className="absolute inset-0 rounded-2xl bg-white opacity-0" />
                      </div>
                    );
                  }),
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Colourful aurora — pools of warm + cool light, screened over
          the wall so it lifts rather than muddies. (The sign-in finale
          fades it out first — [data-aurora].) */}
      <div
        data-aurora="1"
        className="absolute inset-0 mix-blend-screen"
        style={{
          background:
            "radial-gradient(42% 34% at 16% 86%, rgba(242,109,60,.34), transparent 70%)," +
            "radial-gradient(38% 30% at 92% 16%, rgba(74,168,214,.30), transparent 70%)," +
            "radial-gradient(42% 32% at 72% 104%, rgba(199,125,255,.26), transparent 70%)",
        }}
      />

      {/* Legibility scrim — page-specific fade so the photos support
          whatever sits on top of them instead of fighting it. */}
      <div data-scrim="1" className="absolute inset-0" style={{ background: scrim }} />

      <style>{`
        @keyframes authScrollUp { from { transform: translateY(0); } to { transform: translateY(-50%); } }
        @keyframes authScrollDown { from { transform: translateY(-50%); } to { transform: translateY(0); } }
        .auth-col-up { animation: authScrollUp linear infinite; will-change: transform; }
        .auth-col-down { animation: authScrollDown linear infinite; will-change: transform; }
        @media (prefers-reduced-motion: reduce) {
          .auth-col-up, .auth-col-down { animation: none; }
        }
      `}</style>
    </div>
  );
}

export default AuthCollageBackground;
